/**
 * StudyGuidePlayer — audio Study Guide player mounted below the lesson
 * video (DEC-0033..DEC-0036).
 *
 * Responsibilities:
 * - Fetch the guide + caller's progress for the given module.
 * - HTML5 audio playback with custom transport bar.
 * - Resume-on-load logic with "Resuming at m:ss" + Start Over.
 * - Save cadence: pause, debounced seek, ended, visibilitychange,
 *   15s heartbeat, sendBeacon on beforeunload/pagehide (DEC-0035).
 * - 403 recovery — when a presigned URL expires mid-listen, re-fetch
 *   the guide, swap src, restore playhead, resume if was playing.
 * - Inline transcript accordion when the transcript URL is present.
 *
 * Accessibility: full keyboard control, visible focus rings (Tailwind
 * tokens from the existing player surface), aria-live region for the
 * resume + toast messages, descriptive labels on transport buttons.
 *
 * Not handled here:
 * - Auth/enrollment redirects — caller decides what to do with a 401.
 * - Study-guide completion XP — fires server-side on the false→true
 *   transition (DEC-0036). The toast that announces a newly-earned
 *   badge already lives in BadgeToast at the App layout level.
 */
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "@/lib/api";
import {
  beaconProgress,
  getByModule,
  getProgress,
  putProgress,
  type StudyGuide,
  type StudyGuideProgress,
} from "@/lib/study-guide-api";

const HEARTBEAT_INTERVAL_MS = 15_000;
const SEEK_DEBOUNCE_MS = 250;
/** If the saved position is within this many seconds of the end, start fresh. */
const TAIL_GUARD_SECONDS = 5;
const SKIP_SECONDS = 15;
const SPEED_OPTIONS = [0.75, 1.0, 1.25, 1.5, 2.0] as const;

type LoadState =
  | { kind: "loading" }
  | { kind: "absent" } // 404 — no guide for this module
  | { kind: "ready"; guide: StudyGuide; progress: StudyGuideProgress }
  | { kind: "error"; message: string };

export interface StudyGuidePlayerProps {
  /** The module whose Study Guide we're rendering. */
  moduleId: string;
  /** Test seams. */
  getByModuleFn?: typeof getByModule;
  getProgressFn?: typeof getProgress;
  putProgressFn?: typeof putProgress;
  beaconProgressFn?: typeof beaconProgress;
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function StudyGuidePlayer({
  moduleId,
  getByModuleFn = getByModule,
  getProgressFn = getProgress,
  putProgressFn = putProgress,
  beaconProgressFn = beaconProgress,
}: StudyGuidePlayerProps): React.JSX.Element | null {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [resumeNotice, setResumeNotice] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [bufferedEnd, setBufferedEnd] = useState(0);
  const [speed, setSpeed] = useState<number>(1.0);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  // Refs that the audio element + side-effects share. Storing the
  // mutable "furthest" + "completed" snapshot in a ref means save
  // handlers don't capture stale state from useEffect closures.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const furthestRef = useRef(0);
  const positionRef = useRef(0);
  const completedRef = useRef(false);
  const wasPlayingBeforeReloadRef = useRef(false);
  const seekDebounceRef = useRef<number | null>(null);
  const studyGuideIdRef = useRef<string | null>(null);

  // ---------------------------------------------------------------
  // Initial fetch
  // ---------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    void (async () => {
      try {
        const guide = await getByModuleFn(moduleId);
        const progress = await getProgressFn(guide.id);
        if (cancelled) return;
        furthestRef.current = progress.furthest_seconds;
        completedRef.current = progress.completed;
        studyGuideIdRef.current = guide.id;
        setState({ kind: "ready", guide, progress });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setState({ kind: "absent" });
          return;
        }
        const msg =
          err instanceof Error ? err.message : "Could not load the study guide.";
        setState({ kind: "error", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [moduleId, getByModuleFn, getProgressFn]);

  // ---------------------------------------------------------------
  // Save helper — used by every cadence trigger
  // ---------------------------------------------------------------
  const saveProgress = useCallback(async (): Promise<void> => {
    const sgId = studyGuideIdRef.current;
    if (!sgId) return;
    try {
      const updated = await putProgressFn(sgId, {
        position_seconds: Math.floor(positionRef.current),
        furthest_seconds: Math.floor(furthestRef.current),
      });
      // Reflect server-side completion flip in our local refs so the
      // next save doesn't re-emit a stale completed=false.
      completedRef.current = updated.completed;
      furthestRef.current = updated.furthest_seconds;
    } catch {
      // Best-effort save — never alert the learner. Next save will
      // retry the same position/furthest pair.
    }
  }, [putProgressFn]);

  // ---------------------------------------------------------------
  // beforeunload / pagehide → sendBeacon (DEC-0035)
  // ---------------------------------------------------------------
  useEffect(() => {
    if (state.kind !== "ready") return;
    const handler = (): void => {
      const sgId = studyGuideIdRef.current;
      if (!sgId) return;
      beaconProgressFn(sgId, {
        position_seconds: Math.floor(positionRef.current),
        furthest_seconds: Math.floor(furthestRef.current),
      });
    };
    window.addEventListener("beforeunload", handler);
    window.addEventListener("pagehide", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
      window.removeEventListener("pagehide", handler);
    };
  }, [state.kind, beaconProgressFn]);

  // ---------------------------------------------------------------
  // visibilitychange → save when tab goes hidden
  // ---------------------------------------------------------------
  useEffect(() => {
    if (state.kind !== "ready") return;
    const handler = (): void => {
      if (document.visibilityState === "hidden") {
        void saveProgress();
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [state.kind, saveProgress]);

  // ---------------------------------------------------------------
  // 15s heartbeat while playing
  // ---------------------------------------------------------------
  useEffect(() => {
    if (state.kind !== "ready" || !isPlaying) return;
    const id = window.setInterval(() => {
      void saveProgress();
    }, HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [state.kind, isPlaying, saveProgress]);

  // ---------------------------------------------------------------
  // Audio element event handlers
  // ---------------------------------------------------------------
  const handleLoadedMetadata = useCallback((): void => {
    if (state.kind !== "ready") return;
    const audio = audioRef.current;
    if (!audio) return;

    const { guide, progress } = state;
    audio.playbackRate = speed;
    const nearEnd = progress.position_seconds >= guide.duration_seconds - TAIL_GUARD_SECONDS;
    if (progress.completed || nearEnd) {
      audio.currentTime = 0;
      positionRef.current = 0;
      setPosition(0);
      if (progress.completed) {
        setResumeNotice("You finished this — playing from the start.");
      }
    } else if (progress.position_seconds > 0) {
      audio.currentTime = progress.position_seconds;
      positionRef.current = progress.position_seconds;
      setPosition(progress.position_seconds);
      setResumeNotice(`Resuming at ${formatTime(progress.position_seconds)}`);
    }
  }, [state, speed]);

  const handleTimeUpdate = useCallback((): void => {
    const audio = audioRef.current;
    if (!audio) return;
    positionRef.current = audio.currentTime;
    setPosition(audio.currentTime);
    if (audio.currentTime > furthestRef.current) {
      furthestRef.current = audio.currentTime;
    }
  }, []);

  const handleProgressEvent = useCallback((): void => {
    const audio = audioRef.current;
    if (!audio) return;
    const buffered = audio.buffered;
    if (buffered.length > 0) {
      setBufferedEnd(buffered.end(buffered.length - 1));
    }
  }, []);

  const handlePlay = useCallback((): void => {
    setIsPlaying(true);
    setResumeNotice(null);
  }, []);

  const handlePause = useCallback((): void => {
    setIsPlaying(false);
    void saveProgress();
  }, [saveProgress]);

  const handleEnded = useCallback((): void => {
    setIsPlaying(false);
    void saveProgress();
  }, [saveProgress]);

  const handleSeeked = useCallback((): void => {
    if (seekDebounceRef.current !== null) {
      window.clearTimeout(seekDebounceRef.current);
    }
    seekDebounceRef.current = window.setTimeout(() => {
      void saveProgress();
      seekDebounceRef.current = null;
    }, SEEK_DEBOUNCE_MS);
  }, [saveProgress]);

  // 403 recovery: presigned URL expired mid-listen. Re-fetch the
  // guide endpoint, swap src, restore playhead, resume if playing.
  const handleAudioError = useCallback((): void => {
    if (state.kind !== "ready") return;
    const audio = audioRef.current;
    if (!audio) return;
    const savedPosition = audio.currentTime;
    const wasPlaying = !audio.paused;
    wasPlayingBeforeReloadRef.current = wasPlaying;

    void (async () => {
      try {
        const refreshed = await getByModuleFn(moduleId);
        audio.src = refreshed.audio_url;
        audio.load();
        const restoreOnce = (): void => {
          audio.currentTime = savedPosition;
          audio.removeEventListener("loadedmetadata", restoreOnce);
          if (wasPlayingBeforeReloadRef.current) {
            void audio.play();
          }
        };
        audio.addEventListener("loadedmetadata", restoreOnce);
        // Update state so the new presigned URL persists across renders.
        setState((prev) =>
          prev.kind === "ready" ? { ...prev, guide: refreshed } : prev,
        );
      } catch {
        // If recovery itself fails, leave the error visible — better
        // than silently reloading on every error event.
      }
    })();
  }, [state.kind, moduleId, getByModuleFn]);

  // ---------------------------------------------------------------
  // Transport actions
  // ---------------------------------------------------------------
  const togglePlay = useCallback((): void => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      void audio.play();
    } else {
      audio.pause();
    }
  }, []);

  const skipBy = useCallback((delta: number): void => {
    const audio = audioRef.current;
    if (!audio) return;
    if (state.kind !== "ready") return;
    const next = Math.min(
      state.guide.duration_seconds,
      Math.max(0, audio.currentTime + delta),
    );
    audio.currentTime = next;
  }, [state]);

  const onScrubberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const audio = audioRef.current;
      if (!audio) return;
      audio.currentTime = Number(e.target.value);
    },
    [],
  );

  const onSpeedChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>): void => {
      const next = Number(e.target.value);
      setSpeed(next);
      if (audioRef.current) {
        audioRef.current.playbackRate = next;
      }
    },
    [],
  );

  const startOver = useCallback((): void => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    positionRef.current = 0;
    setPosition(0);
    setResumeNotice(null);
  }, []);

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------
  const duration = useMemo(() => {
    return state.kind === "ready" ? state.guide.duration_seconds : 0;
  }, [state]);

  if (state.kind === "loading") {
    return (
      <section
        className="mt-6 rounded-lg border border-border bg-card p-4"
        aria-busy="true"
        aria-label="Study Guide"
      >
        <p className="text-sm text-muted-foreground" role="status">
          Loading study guide…
        </p>
      </section>
    );
  }

  if (state.kind === "absent") {
    // No guide for this module — render nothing rather than empty space.
    return null;
  }

  if (state.kind === "error") {
    return (
      <section
        className="mt-6 rounded-lg border border-red-300 bg-red-50 p-4"
        aria-label="Study Guide"
      >
        <p className="text-sm text-red-700" role="alert">
          {state.message}
        </p>
      </section>
    );
  }

  const { guide } = state;
  const progressPct = duration > 0 ? (position / duration) * 100 : 0;
  const bufferedPct = duration > 0 ? (bufferedEnd / duration) * 100 : 0;

  return (
    <section
      className="mt-6 rounded-lg border border-border bg-card p-4"
      aria-label="Study Guide audio player"
    >
      <header className="mb-3 flex items-baseline justify-between">
        <h2 className="text-base font-semibold text-foreground">
          Audio Study Guide
        </h2>
        <p className="text-xs text-muted-foreground">{guide.title}</p>
      </header>

      <div role="status" aria-live="polite" className="min-h-[1.5rem]">
        {resumeNotice && (
          <div className="mb-2 flex items-center justify-between rounded bg-muted px-3 py-1 text-xs text-muted-foreground">
            <span>{resumeNotice}</span>
            <button
              type="button"
              onClick={startOver}
              className="ml-3 text-xs underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Start over
            </button>
          </div>
        )}
      </div>

      <audio
        ref={audioRef}
        src={guide.audio_url}
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onProgress={handleProgressEvent}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
        onSeeked={handleSeeked}
        onError={handleAudioError}
      />

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={togglePlay}
          className="rounded-md bg-ca-gradient px-4 py-1.5 text-sm font-semibold text-white shadow-soft transition-all hover:-translate-y-px hover:shadow-card focus:outline-none focus-visible:ring-2 focus-visible:ring-ca-indigo"
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? "Pause" : "Play"}
        </button>
        <button
          type="button"
          onClick={() => skipBy(-SKIP_SECONDS)}
          className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Skip back 15 seconds"
        >
          −15s
        </button>
        <button
          type="button"
          onClick={() => skipBy(SKIP_SECONDS)}
          className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Skip forward 15 seconds"
        >
          +15s
        </button>

        <div className="relative flex-1">
          <div
            className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded bg-muted"
            style={{ width: "100%" }}
            aria-hidden
          />
          <div
            className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded bg-muted-foreground/30"
            style={{ width: `${bufferedPct}%` }}
            aria-hidden
          />
          <div
            className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded bg-ca-gradient"
            style={{ width: `${progressPct}%` }}
            aria-hidden
          />
          <input
            type="range"
            min={0}
            max={duration}
            step={1}
            value={position}
            onChange={onScrubberChange}
            className="relative z-10 h-4 w-full cursor-pointer opacity-0"
            aria-label="Audio scrubber"
          />
        </div>

        <span className="tabular-nums text-xs text-muted-foreground">
          {formatTime(position)} / {formatTime(duration)}
        </span>

        <select
          value={speed}
          onChange={onSpeedChange}
          className="rounded-md border border-border bg-background px-2 py-1 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Playback speed"
        >
          {SPEED_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}×
            </option>
          ))}
        </select>
      </div>

      {guide.transcript_url && (
        <div className="mt-3 border-t border-border pt-3">
          <button
            type="button"
            onClick={() => setTranscriptOpen((v) => !v)}
            aria-expanded={transcriptOpen}
            aria-controls={`study-guide-transcript-${guide.id}`}
            className="text-xs underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {transcriptOpen ? "Hide transcript" : "Show transcript"}
          </button>
          {transcriptOpen && (
            <div
              id={`study-guide-transcript-${guide.id}`}
              className="mt-2 max-h-72 overflow-y-auto rounded border border-border bg-muted/30 p-3 text-xs text-foreground"
            >
              <TranscriptBody url={guide.transcript_url} />
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// Tiny inline fetcher for the transcript text. Kept separate so the
// transcript only loads when the learner asks for it.
function TranscriptBody({ url }: { url: string }): React.JSX.Element {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const body = await resp.text();
        if (cancelled) return;
        setText(body);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load transcript.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (error) {
    return <p role="alert">Could not load transcript: {error}</p>;
  }
  if (text === null) {
    return (
      <p role="status" aria-busy="true">
        Loading transcript…
      </p>
    );
  }
  return <pre className="whitespace-pre-wrap font-sans">{text}</pre>;
}
